import { Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { ApiService } from '@core/services/api.service';
import { TranslateService } from '@ngx-translate/core';
import {
    deleteUserLogin,
    getAppLang,
    getBoxVariant,
    getTimeOutSession,
} from '@shared/functions/common-function';
import { Observable, ReplaySubject } from 'rxjs';
import { map } from 'rxjs/operators';
import { AppStateService } from './shared/services/app-state.service';
@Injectable({
    providedIn: 'root',
})
export class AppService {
    // To maintain the state of application
    appEntityJson = {
        recordings: {},
        clips: {},
        bookmarks: {},
        alerts: {},
        search: {},
        frames: {},
        settingsLeftMenuList: {},
    };
    public appSubject = new ReplaySubject<any>();
    public appObservable = this.appSubject.asObservable();
    boxVariant: string = getBoxVariant();
    constructor(
        private apiSrvc: ApiService,
        public translate: TranslateService,
        private snackBar: MatSnackBar,
        private appState: AppStateService,
        public router: Router
    ) {
        translate.addLangs(['en', 'ar']);
        translate.setDefaultLang(getAppLang());
        this.inactivityTime(getTimeOutSession());
        // const browserLang = translate.getBrowserLang();
        // translate.use(browserLang.match(/en|ar/) ? browserLang : 'en');
    }

    openSnackBar(message: string, action: string, duration?, hPosition?, vPosition?): void {
        this.snackBar.open(message, action, {
            duration: duration || 5000,
            horizontalPosition: hPosition || 'right',
            // horizontalPosition: 'end',
            verticalPosition: vPosition || 'top',
            panelClass: ['open-snackbar']
        });
    }

    closeSnackBar() {
        this.snackBar.dismiss();
    }

    updateAppLang(): void {
        this.translate.use(getAppLang() || 'en');
    }

    updateUserHistory(page, activity, user_id?): void {
        // console.log(pageName, message);
        const params = { page, activity };
        if (user_id) {
            params['user_id'] = user_id;
        }

        // ** commenting the previous code in terms of displaying session expired error message **//
        
        // this.apiSrvc
        //     .apiRequest('POST', '/user-activity', {}, false, params)
        //     .toPromise();

        const userActivity = this.apiSrvc
            .apiRequest('POST', '/user-activity', {}, false, params)
            .toPromise();
        userActivity.then((response) => {
            if(response && response.status === 401) {
                console.log('response', response);
                alert('Session Expired, Please login again !!');
                // window.location.reload();
            }
        });
    }

    retrieveEntityData(entityType: string) {
        return this.appEntityJson[entityType];
    }

    storeEntityData(entityType, val) {
        this.appEntityJson[entityType] = val;
    }

    clearEntityData(entityType): void {
        this.appEntityJson[entityType] = {};
    }

    // get filtermenu list of channels and groups
    getChannelsList(params) {
        return this.apiSrvc.apiRequest('GET', '/channels', {}, false, {
            ...params,
        });
        // return this.http.get('assets/channels.json');
    }
    getChannelsGroupList(params) {
        return this.apiSrvc.apiRequest('GET', '/channel-groups', {}, false, {
            ...params,
        });
    }
    getTriggers() {
        return this.apiSrvc.apiRequest('GET', '/alert-triggers', {}, false);
    }
    // get scrollingList
    getScrollingData(url, params): Observable<any> {
        return this.apiSrvc.apiRequest('GET', url, {}, false, params);
        //return this.http.get(url);
    }

    // It is to get the left side menu list
    fetchSettingMenuList(): Observable<any> {
        return this.apiSrvc.apiRequest(
            'GET',
            '/settings_entity_list',
            {},
            false
        );
    }

    // checking auto log off
    inactivityTime(minutes: number): void {
        let time;
        if (minutes && typeof minutes === 'number') {
            window.onload = resetTimer.bind(this);
            // DOM Events
            document.onmousemove = resetTimer.bind(this);
            document.onkeypress = resetTimer.bind(this);

            function resetTimer() {
                window.clearTimeout(time);

                time = setTimeout(
                    () => {
                        deleteUserLogin();
                        this.router.navigate(['./login']);
                        document.onmousemove = null;
                        document.onkeypress = null;
                    },
                    minutes * 60 * 1000,
                );
            }
        }
    }
    // channels API
    channelAPI(crudType, APIname, data, params): Observable<any> {
        switch (crudType) {
            case 'List':
                return this.apiSrvc.apiRequest(
                    'GET',
                    `/${APIname}`,
                    {},
                    false,
                    params
                );
                break;
        }
    }
    getMiqVersion(): Promise<any> {
        return this.apiSrvc
            .apiRequest('GET', '/miq-version', {}, false, {})
            .toPromise();
    }
    deleteBookmark(params): Observable<any> {
        return this.apiSrvc.apiRequest(
            'DELETE',
            '/bookmarks/1',
            {},
            false,
            params
        );
    }
    deleteClip(params): Observable<any> {
        return this.apiSrvc.apiRequest('DELETE', '/clips/1', {}, false, params);
    }
    deleteSearchResults(params): Observable<any> {
        return this.apiSrvc.apiRequest(
            'DELETE',
            '/search/1',
            {},
            false,
            params
        );
    }
    getEncodingsList(params): Observable<any> {
        return this.apiSrvc.apiRequest(
            'GET',
            '/encoding-profiles',
            {},
            false,
            params
        );
    }
    getInprogressClip(params): Observable<any> {
        return this.apiSrvc.apiRequest('GET', '/clips/all', {}, false, params);
    }
    getNSF(): Observable<any> {
        return this.apiSrvc.apiRequest('GET', '/nsf', {}, false, {});
    }
    getFTP(): Observable<any> {
        return this.apiSrvc.apiRequest('GET', '/ftp-export', {}, false, {});
    }

    getTimeZone(): Observable<any> {
        return this.apiSrvc.apiRequest('GET', '/timezone', {}, false, {}).pipe(
            map((result) => {
                if (result.status === 200) {
                    this.appState.setTimezone(result.data.tz);
                }
                return result;
            })
        );
    }

    getAlerts(): Observable<any> {
        let extraParams = this.boxVariant == 'stb_monitor' ? 'localdata=true': 'localdata=false';
        return this.apiSrvc.apiRequest('GET', `/alerts?${extraParams}&limit=5`, {}, false, {});
    }

    getUserPreferences(): Observable<any> {
        return this.apiSrvc.apiRequest(
            'GET',
            '/user-preferences',
            {},
            false,
            {}
        );
    }

    updateUserPreference(id, params?, body?): Promise<any> {
        return this.apiSrvc.apiRequest(
            'PUT',
            `/user-preferences/${id}`,
            body,
            false,
            params
        ).toPromise();
    }

    getUserPreference(params): Observable<any> {
        return this.apiSrvc.apiRequest('GET', '/user-preferences-page', {}, false, params);
    }
}
